import { PBRMaterial } from '@babylonjs/core/Materials/PBR/pbrMaterial';
import { ShaderLanguage } from '@babylonjs/core/Materials/shaderLanguage';
import { Color3 } from '@babylonjs/core/Maths/math.color';
import { Matrix, Quaternion, Vector3 } from '@babylonjs/core/Maths/math.vector';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { VertexData } from '@babylonjs/core/Meshes/mesh.vertexData';
import '@babylonjs/core/Meshes/thinInstanceMesh';
import { Logger } from '@babylonjs/core/Misc/logger';
import type { Observer } from '@babylonjs/core/Misc/observable';
import type { Scene } from '@babylonjs/core/scene';
import type { KeeperConfig, KeeperData } from '@heartpatch/shared';
import { CONTACT_SHADOW, FINISH_CODE, VINYL, type SquishMove, type SquishyLod } from '../config.js';
import { createContactShadowMesh } from '../contact-shadow.js';
import { batchUploads } from '../batch-uploads.js';
import { eventAttribute, eventRunning, type SquishEvent } from '../motion.js';
import { hollowManArrays } from '../hollow-man/hollow-man.js';
import { partArrays } from '../part-shapes.js';
import { SquishPlugin } from '../squish-plugin.js';
import { KEEPER_LOD } from './keeper-config.js';
import type { KeeperItem } from './keeper-items.js';
import { keeperParams, type KeeperParams, type KeeperShape } from './keeper-params.js';

/**
 * Draws Keepers cheaply, the way `SquishyField` draws squishies (CLAUDE.md
 * rule 8):
 * - one shared vinyl material, the squishies' clearcoat look;
 * - one mesh per primitive shape, shared by every Keeper and drawn with thin
 *   instances, so draw calls don't grow with the number of Keepers (five
 *   shapes plus the shadow, however many Keepers). A costume `model` (the
 *   Hollow Man) is one more shape, drawn only while someone wears it;
 * - colours are per-instance attributes;
 * - cheers (a hop, a wiggle) run in the vertex shader from one uniform, using
 *   the squishies' squash plugin. Keepers never breathe, so an idle Keeper
 *   never asks for a frame (render on demand, tech spec §6).
 *
 * Call `update(now)` from the frame driver while it returns true (a cheer is
 * playing); adding, moving or removing Keepers also needs a redraw
 * (`Stage.invalidate`).
 */

export interface KeeperPlacement {
  readonly x: number;
  readonly z: number;
  /** Ground height. */
  readonly y?: number;
  /** Heading in radians; 0 faces −z (towards the default camera). */
  readonly yaw?: number;
  /**
   * Leans back from the feet, radians, so the face looks up at a camera that
   * looks down on it (the map camera is steep; a chibi's hair would hide it).
   */
  readonly lean?: number;
  readonly scale?: number;
  /**
   * A hop (#317): the body floats this far above `y`, world units, while
   * its contact shadow stays on the ground.
   */
  readonly lift?: number;
  /** Height scale about the feet (1 is none); the width is 1/√squash, keeping the volume. */
  readonly squash?: number;
  /** The contact shadow's width, times its size on the ground (it shrinks as the body rises). */
  readonly shadow?: number;
  /** The contact shadow's opacity, 0–1 (it fades as the body rises, #323). */
  readonly shadowAlpha?: number;
}

export interface KeeperHandle {
  readonly id: number;
  readonly params: KeeperParams;
}

export interface KeeperFieldOptions {
  readonly data: KeeperData;
  readonly lod: SquishyLod;
  /** A soft contact shadow under each Keeper. Default on. */
  readonly shadows?: boolean;
}

export interface KeeperFieldStats {
  readonly keepers: number;
  /** Meshes with at least one instance: the field's draw calls (shadow included). */
  readonly meshes: number;
  /** Thin instances across those meshes. */
  readonly instances: number;
  readonly lod: SquishyLod;
}

interface Instance {
  readonly owner: Keeper;
  /** The piece on the Keeper (kept, so a move only re-multiplies) and in the world. */
  readonly local: Matrix;
  readonly matrix: Matrix;
  readonly color: readonly [number, number, number, number];
  /** `FINISH_CODE` sum: the piece's finish, plus glow. */
  readonly finish: number;
}

interface Keeper {
  readonly handle: KeeperHandle;
  world: Matrix;
  /** The body's foot point (xyz) and height (w), for the squash shader. */
  origin: [number, number, number, number];
  scale: number;
  /** The ground under it (the shadow stays here when it hops), and the shadow's width factor and opacity. */
  ground: number;
  shadow: number;
  shadowAlpha: number;
  event: SquishEvent | null;
  readonly instances: { batch: Batch; instance: Instance }[];
}

interface Batch {
  readonly shape: KeeperShape;
  readonly instances: Instance[];
  mesh: Mesh | null;
  /** Instances came or went: rebuild every buffer. */
  dirty: boolean;
  /** Only placements changed (a walk or a hop, #323): rewrite matrices and origins in place. */
  moved: boolean;
  /** Only cheers changed (#501 G): rewrite the event buffer in place. */
  evented: boolean;
  /** The last uploaded origin buffer, rewritten in place on a move. */
  origins: Float32Array | null;
  /** The last uploaded event buffer, rewritten in place on a cheer. */
  events: Float32Array | null;
}

const DEG = Math.PI / 180;

// Scratch for placing Keepers and their shadows (no allocations while walking, #323).
const PLACE_SCALE = new Vector3();
const PLACE_TURN = new Quaternion();
const PLACE_AT = new Vector3();
const SHADOW_TURN = Quaternion.Identity();
const SHADOW_WORLD = new Matrix();

function linear(rgb: readonly [number, number, number]): [number, number, number, number] {
  const c = new Color3(rgb[0], rgb[1], rgb[2]).toLinearSpace();
  return [c.r, c.g, c.b, 1];
}

export class KeeperField {
  readonly #scene: Scene;
  readonly #data: KeeperData;
  readonly #material: PBRMaterial;
  readonly #plugin: SquishPlugin | null;
  readonly #batches = new Map<KeeperShape, Batch>();
  readonly #keepers = new Map<number, Keeper>();
  readonly #shadow: Mesh | null;
  readonly #warned = new Set<string>();
  readonly #beforeRender: Observer<Scene> | null;
  #lod: SquishyLod;
  #shadowDirty = true;
  /** The shadows' last buffers, rewritten in place while the count holds. */
  #shadowMatrices: Float32Array | null = null;
  #shadowColors: Float32Array | null = null;
  #nextId = 1;
  /** Wall-clock ms at Keeper-clock zero, set by the first `update` or `play`. */
  #clockStart: number | null = null;

  constructor(scene: Scene, options: KeeperFieldOptions) {
    this.#scene = scene;
    this.#data = options.data;
    this.#lod = options.lod;

    const m = new PBRMaterial('keeper-vinyl', scene);
    m.albedoColor = Color3.White(); // per-instance colours multiply this
    m.metallic = 0;
    m.roughness = VINYL.roughness;
    m.clearCoat.isEnabled = true;
    m.clearCoat.intensity = VINYL.clearCoatIntensity;
    m.clearCoat.roughness = VINYL.clearCoatRoughness;
    this.#material = m;
    // GLSL only (see squish-plugin.ts): under WebGPU Keepers don't hop.
    this.#plugin = m.shaderLanguage === ShaderLanguage.GLSL ? new SquishPlugin(m) : null;

    this.#shadow = (options.shadows ?? true) ? createContactShadowMesh(scene) : null;
    this.#beforeRender = scene.onBeforeRenderObservable.add(() => {
      this.flush();
    });
  }

  /** Adds a Keeper; `items` are worn clothing (one per slot). */
  add(
    config: KeeperConfig,
    placement: KeeperPlacement,
    items: readonly KeeperItem[] = [],
  ): KeeperHandle {
    const params = keeperParams(config, this.#data, items);
    for (const id of params.missing) {
      if (this.#warned.has(id)) continue;
      this.#warned.add(id);
      Logger.Warn(`A Keeper uses "${id}", which this client doesn't know.`);
    }
    const handle: KeeperHandle = { id: this.#nextId++, params };
    const keeper: Keeper = {
      handle,
      world: Matrix.Identity(),
      origin: [0, 0, 0, 0],
      scale: 1,
      ground: 0,
      shadow: 1,
      shadowAlpha: 1,
      event: null,
      instances: [],
    };
    this.#keepers.set(handle.id, keeper);
    this.#layout(keeper, placement);
    return handle;
  }

  /** Moves a Keeper: its pieces keep their place on it, so this only re-places them (#323). */
  move(handle: KeeperHandle, placement: KeeperPlacement): void {
    const keeper = this.#keepers.get(handle.id);
    if (!keeper) return;
    this.#place(keeper, placement);
    for (const { batch, instance } of keeper.instances) {
      instance.local.multiplyToRef(keeper.world, instance.matrix);
      batch.moved = true;
    }
    this.#shadowDirty = true;
  }

  remove(handle: KeeperHandle): void {
    const keeper = this.#keepers.get(handle.id);
    if (!keeper) return;
    this.#detach(keeper);
    this.#keepers.delete(handle.id);
  }

  /** Removes every Keeper. */
  clear(): void {
    for (const keeper of this.#keepers.values()) this.#detach(keeper);
    this.#keepers.clear();
  }

  /** A cheer: `bounce` is a happy jump, `jiggle` a wiggle, `wobble` an "aww". `now` is wall-clock ms. */
  play(handle: KeeperHandle, move: SquishMove, now: number, strength = 1): void {
    const keeper = this.#keepers.get(handle.id);
    if (!keeper) return;
    keeper.event = { move, start: this.#clock(now), strength };
    // Only the event attribute changes: rewritten in place, no new buffers (#501 G).
    for (const { batch } of keeper.instances) batch.evented = true;
  }

  /** Advances the Keeper clock; true while a cheer plays (keep drawing), else false. */
  update(now: number): boolean {
    const t = this.#clock(now);
    if (!this.#plugin) return false; // nothing moves without the shader
    this.#plugin.time = t;
    for (const k of this.#keepers.values()) if (eventRunning(k.event, t)) return true;
    return false;
  }

  isPlaying(handle: KeeperHandle, now: number): boolean {
    const keeper = this.#keepers.get(handle.id);
    return keeper !== undefined && eventRunning(keeper.event, this.#clockAt(now));
  }

  /** Switches detail level (`lodFor`); rebuilds the shared geometry only. */
  setLod(lod: SquishyLod): void {
    if (lod === this.#lod) return;
    this.#lod = lod;
    for (const batch of this.#batches.values()) {
      batch.mesh?.dispose();
      batch.mesh = null;
      batch.dirty = true;
    }
  }

  get lod(): SquishyLod {
    return this.#lod;
  }

  get handles(): KeeperHandle[] {
    return [...this.#keepers.values()].map((k) => k.handle);
  }

  /** World position of a Keeper's middle (tests, and pointing the camera). */
  centre(handle: KeeperHandle): Vector3 | null {
    const k = this.#keepers.get(handle.id);
    return k ? new Vector3(k.origin[0], k.origin[1] + k.origin[3] / 2, k.origin[2]) : null;
  }

  get stats(): KeeperFieldStats {
    this.flush();
    let meshes = 0;
    let instances = 0;
    for (const batch of this.#batches.values()) {
      if (batch.instances.length === 0) continue;
      meshes++;
      instances += batch.instances.length;
    }
    if (this.#shadow && this.#keepers.size > 0) {
      meshes++;
      instances += this.#keepers.size;
    }
    return { keepers: this.#keepers.size, meshes, instances, lod: this.#lod };
  }

  /** Uploads changed instance buffers. Runs before every render; call it to force one. */
  flush(): void {
    for (const batch of this.#batches.values()) {
      if (batch.dirty) {
        this.#upload(batch);
        continue;
      }
      if (batch.moved) this.#uploadMoves(batch);
      if (batch.evented) this.#uploadEvents(batch);
    }
    if (this.#shadowDirty) this.#uploadShadows();
  }

  dispose(): void {
    if (this.#beforeRender) this.#scene.onBeforeRenderObservable.remove(this.#beforeRender);
    for (const batch of this.#batches.values()) batch.mesh?.dispose();
    this.#batches.clear();
    this.#keepers.clear();
    if (this.#shadow) {
      this.#shadow.material?.dispose(true, true);
      this.#shadow.dispose();
    }
    this.#material.dispose();
  }

  #clockAt(now: number): number {
    return this.#clockStart === null ? 0 : (now - this.#clockStart) / 1000;
  }

  #clock(now: number): number {
    this.#clockStart ??= now;
    return this.#clockAt(now);
  }

  #batch(shape: KeeperShape): Batch {
    let batch = this.#batches.get(shape);
    if (!batch) {
      batch = {
        shape,
        instances: [],
        mesh: null,
        dirty: true,
        moved: false,
        evented: false,
        origins: null,
        events: null,
      };
      this.#batches.set(shape, batch);
    }
    return batch;
  }

  /** The Keeper's world matrix, origin, ground and shadow, in place. */
  #place(keeper: Keeper, placement: KeeperPlacement): void {
    const scale = placement.scale ?? 1;
    const squash = placement.squash ?? 1;
    const wide = scale / Math.sqrt(squash);
    const ground = placement.y ?? 0;
    PLACE_AT.set(placement.x, ground + (placement.lift ?? 0), placement.z);
    Quaternion.RotationYawPitchRollToRef(placement.yaw ?? 0, placement.lean ?? 0, 0, PLACE_TURN);
    Matrix.ComposeToRef(
      PLACE_SCALE.set(wide, scale * squash, wide),
      PLACE_TURN,
      PLACE_AT,
      keeper.world,
    );
    keeper.scale = scale;
    keeper.ground = ground;
    keeper.shadow = placement.shadow ?? 1;
    keeper.shadowAlpha = placement.shadowAlpha ?? 1;
    const o = keeper.origin;
    o[0] = PLACE_AT.x;
    o[1] = PLACE_AT.y;
    o[2] = PLACE_AT.z;
    o[3] = keeper.handle.params.height * scale;
  }

  #layout(keeper: Keeper, placement: KeeperPlacement): void {
    const { params } = keeper.handle;
    this.#place(keeper, placement);
    for (const p of params.pieces) {
      const local = Matrix.Compose(
        new Vector3(p.size[0], p.size[1], p.size[2]),
        Quaternion.RotationYawPitchRoll(p.turn[1] * DEG, p.turn[0] * DEG, p.turn[2] * DEG),
        new Vector3(p.at[0], p.at[1], p.at[2]),
      );
      const instance: Instance = {
        owner: keeper,
        local,
        matrix: local.multiply(keeper.world),
        color: linear(p.color),
        finish: (p.finish ? FINISH_CODE[p.finish] : 0) + (p.glow ? FINISH_CODE.glow : 0),
      };
      const batch = this.#batch(p.shape);
      batch.instances.push(instance);
      batch.dirty = true;
      keeper.instances.push({ batch, instance });
    }
    this.#shadowDirty = true;
  }

  #detach(keeper: Keeper): void {
    for (const { batch, instance } of keeper.instances) {
      const i = batch.instances.indexOf(instance);
      if (i >= 0) batch.instances.splice(i, 1);
      batch.dirty = true;
    }
    keeper.instances.length = 0;
    this.#shadowDirty = true;
  }

  #upload(batch: Batch): void {
    batch.dirty = false;
    batch.moved = false;
    batch.evented = false;
    const n = batch.instances.length;
    if (n === 0) {
      batch.mesh?.setEnabled(false);
      return;
    }
    let mesh = batch.mesh;
    if (!mesh) {
      mesh = new Mesh(`keeper-${batch.shape}-${this.#lod}`, this.#scene);
      const lod = KEEPER_LOD[this.#lod];
      const arrays =
        batch.shape === 'model-hollow-man'
          ? hollowManArrays(this.#scene, lod.modelSegments)
          : partArrays(batch.shape, lod);
      const data = new VertexData();
      data.positions = arrays.positions;
      data.normals = arrays.normals;
      data.indices = arrays.indices;
      data.applyToMesh(mesh);
      mesh.material = this.#material;
      mesh.isPickable = false;
      // Cheers move vertices past the bounds; a handful of meshes isn't worth culling.
      mesh.alwaysSelectAsActiveMesh = true;
      mesh.freezeWorldMatrix();
      batch.mesh = mesh;
    }
    mesh.setEnabled(true);
    batchUploads.count++;

    const matrices = new Float32Array(n * 16);
    const colors = new Float32Array(n * 4);
    const origins = new Float32Array(n * 4);
    const motions = new Float32Array(n * 4); // no breathing: Keepers stand still
    const events = new Float32Array(n * 4);
    batch.instances.forEach((inst, i) => {
      inst.matrix.copyToArray(matrices, i * 16);
      colors.set(inst.color, i * 4);
      origins.set(inst.owner.origin, i * 4);
      events.set(eventAttribute(inst.owner.event), i * 4);
      events[i * 4 + 3] = inst.finish;
    });
    mesh.thinInstanceSetBuffer('matrix', matrices, 16, false);
    mesh.thinInstanceSetBuffer('color', colors, 4, true);
    mesh.thinInstanceSetBuffer('squishOrigin', origins, 4, false);
    mesh.thinInstanceSetBuffer('squishMotion', motions, 4, true);
    mesh.thinInstanceSetBuffer('squishEvent', events, 4, false);
    batch.origins = origins;
    batch.events = events;
  }

  /** Rewrites a batch's matrices and origins in place after moves (no new buffers). */
  #uploadMoves(batch: Batch): void {
    batch.moved = false;
    const { mesh, origins } = batch;
    if (!mesh || !origins) return;
    batch.instances.forEach((inst, i) => {
      // Through the mesh, so its world-matrix copies (picking) see the move too.
      mesh.thinInstanceSetMatrixAt(i, inst.matrix, false);
      origins.set(inst.owner.origin, i * 4);
    });
    mesh.thinInstanceBufferUpdated('matrix');
    mesh.thinInstanceBufferUpdated('squishOrigin');
  }

  /** Rewrites a batch's cheers in place (no new buffers, #501 G: a find's cheer made ~30). */
  #uploadEvents(batch: Batch): void {
    batch.evented = false;
    const { mesh, events } = batch;
    if (!mesh || !events) return;
    batch.instances.forEach((inst, i) => {
      events.set(eventAttribute(inst.owner.event), i * 4);
      events[i * 4 + 3] = inst.finish;
    });
    mesh.thinInstanceBufferUpdated('squishEvent');
  }

  #uploadShadows(): void {
    this.#shadowDirty = false;
    const shadow = this.#shadow;
    if (!shadow) return;
    const n = this.#keepers.size;
    shadow.setEnabled(n > 0);
    if (n === 0) return;
    // Same count: rewrite the buffers in place (a walk or a hop moves them every frame).
    const kept = this.#shadowMatrices?.length === n * 16 ? this.#shadowMatrices : null;
    const fresh = kept === null || this.#shadowColors === null;
    const matrices = fresh ? new Float32Array(n * 16) : kept;
    const colors = fresh || !this.#shadowColors ? new Float32Array(n * 4) : this.#shadowColors;
    let i = 0;
    for (const k of this.#keepers.values()) {
      const d = k.handle.params.width * k.scale * CONTACT_SHADOW.scale * k.shadow;
      Matrix.ComposeToRef(
        PLACE_SCALE.set(d, 1, d),
        SHADOW_TURN,
        PLACE_AT.set(k.origin[0], k.ground + 0.01, k.origin[2]),
        SHADOW_WORLD,
      ).copyToArray(matrices, i * 16);
      colors[i * 4] = 1;
      colors[i * 4 + 1] = 1;
      colors[i * 4 + 2] = 1;
      colors[i * 4 + 3] = k.shadowAlpha;
      i++;
    }
    if (fresh) {
      this.#shadowMatrices = matrices;
      this.#shadowColors = colors;
      shadow.thinInstanceSetBuffer('matrix', matrices, 16, false);
      shadow.thinInstanceSetBuffer('color', colors, 4, false);
    } else {
      shadow.thinInstanceBufferUpdated('matrix');
      shadow.thinInstanceBufferUpdated('color');
    }
  }
}
